/**
 * Types for TerraformVariablesEditor
 *
 * These mirror the backend types for use in the frontend.
 */

export type TerraformPrimitiveType = 'string' | 'number' | 'bool';
export type TerraformCollectionType = 'list' | 'set' | 'map';
export type TerraformStructuralType = 'object' | 'tuple';
export type TerraformType =
  | TerraformPrimitiveType
  | TerraformCollectionType
  | TerraformStructuralType
  | 'any'
  | 'dynamic';

export interface ValidationRule {
  condition: string;
  errorMessage: string;
  pattern?: string;
  enum?: string[];
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
}

export interface TerraformVariableSchema {
  name: string;
  type: TerraformType;
  description?: string;
  default?: unknown;
  currentValue?: unknown;
  required: boolean;
  sensitive: boolean;
  optional?: boolean;
  /** Whether this field can be null (Terraform nullable = true) */
  nullable?: boolean;
  validation?: ValidationRule[];
  objectSchema?: Record<string, TerraformVariableSchema>;
  elementType?: TerraformType;
  elementSchema?: TerraformVariableSchema;
  tupleElements?: TerraformVariableSchema[];
  rawType?: string;
}

export interface ParseTerraformResponse {
  variables: TerraformVariableSchema[];
  warnings?: string[];
  sourceFiles: {
    variablesFile?: string;
    tfvarsFile?: string;
  };
}

export interface TerraformVariablesEditorOptions {
  /** Path to terraform directory or file */
  terraformPath?: string;
  /** Path parameter name that contains the terraform path */
  terraformPathParam?: string;
  /** Path to tfvars file */
  tfvarsPath?: string;
  /** Path parameter name that contains the tfvars path */
  tfvarsPathParam?: string;
  /** Only show these variables */
  variableFilter?: string[];
  /** Hide sensitive variables */
  hideSensitive?: boolean;
  /** Collapse object fields by default */
  collapseObjects?: boolean;
  /** Show descriptions inline */
  showDescriptions?: boolean;
  /** Show current values from tfvars */
  showCurrentValues?: boolean;
  /** Index signature for UIOptionsType compatibility */
  [key: string]: string | number | boolean | object | any[] | null | undefined;
}

export interface VariableEditorProps {
  variable: TerraformVariableSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  /** Nesting depth for visual indentation (default: 0) */
  depth?: number;
}

/**
 * Context for editor-wide settings like show/hide hidden values
 */
export interface EditorContextValue {
  /** Whether to show hidden/null values */
  showHidden: boolean;
  /** Toggle show hidden state */
  setShowHidden: (show: boolean) => void;
}

/**
 * Check if a value appears to be derived from another variable
 * e.g., "${var.tfc_org}" or "prefix-${var.name}-suffix"
 */
export function isDerivedValue(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  // Match ${var.xxx} or ${local.xxx} patterns
  return /\$\{(var|local|data|module)\.[^}]+\}/.test(value);
}

/**
 * Check if a value should be considered "hidden" (null or derived)
 */
export function isHiddenValue(
  value: unknown,
  schema: TerraformVariableSchema,
): boolean {
  if (value === null && schema.nullable) return true;
  if (isDerivedValue(value)) return true;
  return false;
}
